export const PUBLIC_PROVIDERS = Object.freeze([
  {
    slug: "aws",
    name: "AWS",
    eyebrow: "AWS certification preparation",
    description: "Prepare for AWS certifications with scenario-based practice, detailed reasoning, practical Labs where available, full exam simulation, and performance-guided follow-up.",
    color: "#ff9900",
  },
  {
    slug: "microsoft",
    name: "Microsoft",
    eyebrow: "Microsoft certification preparation",
    description: "Build exam knowledge through realistic questions, explanation-led review, targeted practice, exam simulation, and certification-specific performance analysis.",
    color: "#38bdf8",
  },
  {
    slug: "google-cloud",
    name: "Google Cloud",
    eyebrow: "Google Cloud certification preparation",
    description: "Prepare for Google Cloud certification with realistic operational scenarios, focused practice, practical Labs, and evidence that guides the next session.",
    color: "#60a5fa",
  },
  {
    slug: "cisco",
    name: "Cisco",
    eyebrow: "Cisco certification preparation",
    description: "Practise networking decisions, apply configuration skills in browser-based Labs, simulate the exam, and use performance evidence to focus your preparation.",
    color: "#22d3ee",
  },
  {
    slug: "comptia",
    name: "CompTIA",
    eyebrow: "CompTIA certification preparation",
    description: "Prepare for CompTIA certification with original scenarios, detailed option analysis, practical security Labs, exam simulation, and targeted follow-up.",
    color: "#f87171",
  },
]);

const PROVIDER_ALIASES = Object.freeze({
  aws: "AWS",
  amazon: "AWS",
  "amazon web services": "AWS",
  azure: "Microsoft",
  "microsoft azure": "Microsoft",
  microsoft: "Microsoft",
  google: "Google Cloud",
  gcp: "Google Cloud",
  "google cloud": "Google Cloud",
  cisco: "Cisco",
  comptia: "CompTIA",
});

// These published URLs remain valid while other providers use one shared route.
export const LEGACY_PROVIDER_SLUGS = Object.freeze(["aws", "azure", "google-cloud", "cisco", "comptia"]);
const AZURE_PROVIDER = Object.freeze({
  slug: "azure",
  name: "Microsoft Azure",
  eyebrow: "Microsoft Azure certification preparation",
  description: "Build Azure exam knowledge through realistic questions, explanation-led review, targeted practice, exam simulation, and certification-specific performance analysis.",
  color: "#38bdf8",
});

export const normalizeProviderName = (value = "") => {
  const name = String(value || "").trim();
  return PROVIDER_ALIASES[name.toLowerCase()] || name || "Other";
};

const isActive = (certification) => (
  certification.is_active !== false && certification.active !== false && certification.isActive !== false
);

export const providerPath = (slug) => {
  const normalized = String(slug || "").trim().toLowerCase();
  if (!normalized) return "/certifications/";
  return LEGACY_PROVIDER_SLUGS.includes(normalized)
    ? `/${normalized}/`
    : `/providers/${encodeURIComponent(normalized)}/`;
};

export const providerForCertification = (certification = {}) => {
  const name = normalizeProviderName(certification.provider || certification.vendor);
  const known = PUBLIC_PROVIDERS.find((provider) => provider.name === name);
  if (known) return known;
  const slug = name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "") || "other";
  return {
    slug,
    name,
    eyebrow: `${name} certification preparation`,
    description: `Prepare for ${name} certifications with original practice questions, detailed reasoning, exam simulation, and performance-guided follow-up.`,
    color: /^#[0-9a-f]{6}$/i.test(certification.provider_color || "") ? certification.provider_color : "#38bdf8",
  };
};

export const certificationsForProvider = (certifications, provider) => {
  if (!provider) return [];
  return (certifications || []).filter((certification) => {
    if (!isActive(certification)) return false;
    const identity = providerForCertification(certification);
    if (provider.slug === "azure") {
      return identity.slug === "microsoft" && String(certification.code || certification.id || certification.cert_code || "").trim().toUpperCase().startsWith("AZ-");
    }
    return identity.slug === provider.slug;
  });
};

export const groupCertificationsByProvider = (certifications) => {
  const groups = new Map();
  (certifications || []).filter(isActive).forEach((certification) => {
    const provider = providerForCertification(certification);
    if (!groups.has(provider.slug)) groups.set(provider.slug, { provider, certifications: [] });
    groups.get(provider.slug).certifications.push(certification);
  });
  const rank = (slug) => {
    const index = PUBLIC_PROVIDERS.findIndex((provider) => provider.slug === slug);
    return index < 0 ? PUBLIC_PROVIDERS.length : index;
  };
  return [...groups.values()].sort((left, right) => (
    rank(left.provider.slug) - rank(right.provider.slug) || left.provider.name.localeCompare(right.provider.name)
  ));
};

export const providerFromSlug = (slug, certifications = []) => {
  const normalized = String(slug || "").trim().toLowerCase();
  if (normalized === "azure") return AZURE_PROVIDER;
  const known = PUBLIC_PROVIDERS.find((provider) => provider.slug === normalized);
  if (known && LEGACY_PROVIDER_SLUGS.includes(normalized)) return known;
  return groupCertificationsByProvider(certifications).find(({ provider }) => provider.slug === normalized)?.provider || null;
};
